import { BorderColorRounded, CheckCircleOutlined } from '@mui/icons-material'
import { Box, Button, Divider, LinearProgress, Stack, Typography } from '@mui/material'
import { FC, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { LabeledText } from '../../../../components/CustomTypography'
import { RightSlider } from '../../../../components/sliders'
import { StyledCard } from '../../../../components/StyledCard'
import { AppDispatch } from '../../../../store'
import { CurrentUserActions, CurrentUserSelector } from '../../../../store/currentUser'
import { MetricsSelector } from '../../../../store/TechnologyMetrics'
import { FundingAmountForm } from './FundingAmountForm'

interface FundingGoalStatsProps {
  showActionButtons?: boolean
}

export const FundingGoalStats: FC<FundingGoalStatsProps> = ({ showActionButtons }) => {
  const dispatch = useDispatch<AppDispatch>()

  const [showFundingForm, setShowFundingForm] = useState(false)

  const grantsAwarded = useSelector(MetricsSelector.makeWonCount)
  const fundingSecured = useSelector(MetricsSelector.makeTotalWonAmount)
  const fundingAmountGoal = useSelector(CurrentUserSelector.makeCompanyFundingAmount)

  const formattedGoalAmount = fundingAmountGoal.toLocaleString('en-US')
  const formattedSecuredAmount = fundingSecured.toLocaleString('en-US')

  const rawProgress = fundingAmountGoal > 0 ? (fundingSecured / fundingAmountGoal) * 100 : 0

  const progressPercentage = Math.min(rawProgress, 100)

  useEffect(() => {
    dispatch(CurrentUserActions.fetchCurrentUserCompanyAsync())
  }, [dispatch])

  const onSliderClose = () => {
    setShowFundingForm(false)
  }

  return (
    <>
      <RightSlider
        open={showFundingForm}
        setOpen={setShowFundingForm}
        contentContainerSx={{ py:0 }}
        outerBoxSx={{ width: '600px' }}
        closeButtonAction={onSliderClose}
      >
        <FundingAmountForm currentAmount={fundingAmountGoal.toString()} onClose={()=> setShowFundingForm(false)} />
      </RightSlider>

      <StyledCard
        sx={{
          display: 'flex',
          cursor: 'pointer',
          position: 'relative',
          justifyContent: 'space-between',
        }}
      >
        <Stack direction="row" width="85%" gap={5}>
          <Box width="30%">
            <LabeledText
              label="Funding secured"
              valueProps={{ fontSize: '1.5rem' }}
              labelProps={{ fontSize: '1rem', fontWeight: 500 }}
              value={`$${formattedSecuredAmount}`}
              rootContainerProps={{
                gap: 2,
                direction: 'column',
                alignItems: 'flex-start',
              }}
            />

            <Stack mt={0.5} direction="row" alignItems="center" gap={0.5}>
              <CheckCircleOutlined color="primary" />

              <Typography fontWeight={500} color="primary" fontSize="0.9rem">
                {`${grantsAwarded} grants awarded`}
              </Typography>
            </Stack>
          </Box>

          <Divider
            flexItem
            orientation="vertical"
            sx={{ background: (theme) => theme.palette.grey[200] }}
          />

          <Stack width="60%">
            <Typography fontSize="1rem" color="secondary.light" fontWeight={500}>
              Funding goal for the year
            </Typography>

            <Box mt={3} width="100%">
              <LinearProgress
                variant="determinate"
                value={progressPercentage}
                sx={{
                  height: 10,
                  borderRadius: 5,
                }}
              />
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography
                  fontSize="1rem"
                  color="secondary"
                  fontWeight={600}
                  letterSpacing="0.5px"
                >
                  {`$${formattedSecuredAmount} / $${formattedGoalAmount}`}
                </Typography>

                <Typography fontSize="0.9rem" color="secondary.light" fontWeight={500}>
                  {progressPercentage}%
                </Typography>
              </Stack>
            </Box>
          </Stack>
        </Stack>

        {showActionButtons && (
          <Button
            color="secondary"
            variant="outlined"
            startIcon={<BorderColorRounded />}
            onClick={() => setShowFundingForm(true)}
            sx={{ height: '40px', borderRadius: '8px', textTransform: 'none' }}
          >
            Edit goal
          </Button>
        )}
      </StyledCard>
    </>
  )
}
